import type { ReactNode } from 'react'
import { useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import type { DehydratedState } from '@tanstack/react-query'
import {
  HydrationBoundary,
  QueryClient,
  QueryCache,
  QueryClientProvider as TanStackQueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
interface ErrorQueryMeta {
  onError?: (error: Error) => void
  sentryContext?: string
}

const createQueryCache = (): QueryCache =>
  new QueryCache({
    onError: (error, query) => {
      const { onError, sentryContext } = (query.meta ?? {}) as ErrorQueryMeta
      onError?.(error)
      if (sentryContext) {
        Sentry.captureException(error, { extra: { context: sentryContext } })
      }
    },
  })

const createClientSideQueryClient = (): QueryClient =>
  new QueryClient({
    queryCache: createQueryCache(),
    defaultOptions: {
      queries: {
        gcTime: 5 * 60 * 1000, // 5 minutes
        staleTime: 30 * 1000, // 30 seconds
        retry: 1,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 1,
      },
    },
  })

interface QueryClientProviderProps {
  children: ReactNode
  dehydratedState?: DehydratedState
}

export const QueryClientProvider = ({
  children,
  dehydratedState,
}: QueryClientProviderProps): ReactNode => {
  const [queryClient] = useState(() => createClientSideQueryClient())

  return (
    <TanStackQueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        {children}
        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
        )}
      </HydrationBoundary>
    </TanStackQueryClientProvider>
  )
}
